/*
 * <saint-hover-fill>: Concept's button hover (the curved `.btn-fill` that sweeps across a
 * `.button--primary` and inverts its colours), run with GSAP: on hover or keyboard focus the fill
 * drops in from the top, and on leave it carries on down and out through the bottom (or follows the
 * pointer with data-from="pointer").
 *
 * Place it anywhere inside the region it serves. It listens on `this.closest(data-scope)`:
 *   data-targets  the hover triggers (selector)
 *   data-fill-in  optional: the element inside the trigger that gets the fill (default: the trigger)
 *   data-draw     optional: strokes inside the trigger to redraw on enter, like the header icons
 *   data-from     optional: "pointer" sweeps the fill in from the edge the pointer entered through
 *                 and out through the edge it left by (keyboard focus still runs top to bottom)
 *   data-fill-class optional: extra classes for the fill, e.g. Concept's `btn-fill` to reuse its
 *                 shape and colours on a Concept `.button` (saint-hover-fill.css hides Concept's own)
 *   data-gsap-src GSAP's asset URL, loaded once if the page hasn't loaded GSAP yet
 *
 * The fill span is created here, so the markup (including Concept snippets) stays untouched.
 * The trigger gets `is-saint-filled` while filled; the component CSS sets the inverted colours.
 * With reduced motion the fill appears and goes without moving.
 */
if (!customElements.get('saint-hover-fill')) {
  // Where the fill rests outside each edge, as GSAP percentages of the fill. It's Concept's `.btn-fill`
  // ellipse (150% x 200% of the trigger, centred on it), so 76% clears it vertically and 84% sideways.
  // Inside the guard: several sections load this file, and a second top-level `const` would throw.
  const SAINT_FILL_OFFSETS = {
    top: { xPercent: 0, yPercent: -76 },
    bottom: { xPercent: 0, yPercent: 76 },
    left: { xPercent: -84, yPercent: 0 },
    right: { xPercent: 84, yPercent: 0 },
  };

  customElements.define(
    'saint-hover-fill',
    class SaintHoverFill extends HTMLElement {
      constructor() {
        super();
        this.onEnter = this.onEnter.bind(this);
        this.onLeave = this.onLeave.bind(this);
      }

      connectedCallback() {
        this.scope = this.closest(this.dataset.scope || 'body');
        if (!this.scope || !this.dataset.targets) return;

        this.loadGsap()
          .then(() => {
            if (this.isConnected) this.setup();
          })
          .catch((error) => console.warn('[saint-hover-fill] GSAP failed to load; hovers use the plain colour change.', error));
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.mm?.revert();
      }

      loadGsap() {
        if (typeof gsap !== 'undefined') return Promise.resolve();
        if (!this.dataset.gsapSrc) return Promise.reject(new Error('missing data-gsap-src'));

        window.saintGsapLoading =
          window.saintGsapLoading ||
          new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = this.dataset.gsapSrc;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        return window.saintGsapLoading;
      }

      setup() {
        this.abortController = new AbortController();
        const { signal } = this.abortController;
        this.scope.addEventListener('pointerover', this.onEnter, { signal });
        this.scope.addEventListener('pointerout', this.onLeave, { signal });
        this.scope.addEventListener('focusin', this.onEnter, { signal });
        this.scope.addEventListener('focusout', this.onLeave, { signal });
        this.scope.classList.add('saint-hover-fill-ready');

        this.mm = gsap.matchMedia();
        this.mm.add({ fine: '(pointer: fine)', reduce: '(prefers-reduced-motion: reduce)' }, (context) => {
          this.fine = context.conditions.fine;
          this.reduce = context.conditions.reduce || theme.config.motionReduced;
        });
      }

      // The trigger the pointer or focus entered or left, ignoring moves between its own children.
      trigger(event) {
        const trigger = event.target.closest?.(this.dataset.targets);
        if (!trigger || !this.scope.contains(trigger) || trigger.contains(event.relatedTarget)) return null;
        return trigger;
      }

      fill(trigger) {
        const host = (this.dataset.fillIn && trigger.querySelector(this.dataset.fillIn)) || trigger;
        let fill = host.querySelector(':scope > .saint-fill');
        if (!fill) {
          fill = document.createElement('span');
          fill.className = `saint-fill ${this.dataset.fillClass || ''}`.trim();
          fill.setAttribute('aria-hidden', 'true');
          host.prepend(fill);
          gsap.set(fill, { ...SAINT_FILL_OFFSETS.top, x: 0, y: 0 });
        }
        return fill;
      }

      // The filled element's edge nearest the pointer: the one it just crossed, coming in or going out.
      // When the fill sits in a smaller element (the mega menu card's pill), that's the side of it the
      // pointer is on, which can be outside it (a negative distance).
      edge(trigger, event, fallback) {
        if (this.dataset.from !== 'pointer' || !event.type.startsWith('pointer')) return fallback;
        const host = (this.dataset.fillIn && trigger.querySelector(this.dataset.fillIn)) || trigger;
        const rect = host.getBoundingClientRect();
        const distances = {
          top: event.clientY - rect.top,
          bottom: rect.bottom - event.clientY,
          left: event.clientX - rect.left,
          right: rect.right - event.clientX,
        };
        return Object.keys(distances).reduce((nearest, side) => (distances[side] < distances[nearest] ? side : nearest));
      }

      onEnter(event) {
        if (event.type === 'pointerover' && !this.fine) return;
        const trigger = this.trigger(event);
        if (!trigger || trigger.classList.contains('is-saint-filled')) return;
        // A disabled button doesn't fill, but one that became disabled while filled still empties on leave.
        if (trigger.matches('[aria-disabled="true"], [disabled]')) return;

        // From the top, or with data-from="pointer" from the side the pointer came in.
        gsap.fromTo(
          this.fill(trigger),
          { ...SAINT_FILL_OFFSETS[this.edge(trigger, event, 'top')], x: 0, y: 0 },
          { xPercent: 0, yPercent: 0, duration: this.reduce ? 0 : 0.39, ease: 'power3.out', overwrite: true }
        );
        this.swapColours(trigger, true);
        this.draw(trigger);
      }

      onLeave(event) {
        const trigger = this.trigger(event);
        if (!trigger || !trigger.classList.contains('is-saint-filled')) return;
        // Stay filled while keyboard focus is still on it.
        if (event.type === 'pointerout' && trigger.matches(':focus-visible')) return;
        if (event.type === 'focusout' && trigger.matches(':hover')) return;

        // On down and out through the bottom, or with data-from="pointer" out the side the pointer left by.
        gsap.to(this.fill(trigger), {
          ...SAINT_FILL_OFFSETS[this.edge(trigger, event, 'bottom')],
          x: 0,
          y: 0,
          duration: this.reduce ? 0 : 0.32,
          ease: 'power3.inOut',
          overwrite: true,
        });
        this.swapColours(trigger, false);
      }

      // Toggles `is-saint-filled`, whose colours live in the component CSS, and tweens the text and
      // border colour from where they are to the new ones in step with the fill, then hands back to CSS.
      swapColours(trigger, filled) {
        const host = (this.dataset.fillIn && trigger.querySelector(this.dataset.fillIn)) || trigger;
        const targets = host === trigger ? [trigger] : [host, trigger];
        const from = targets.map((element) => {
          const style = getComputedStyle(element);
          return { color: style.color, borderColor: style.borderColor };
        });

        gsap.killTweensOf(targets, 'color,borderColor');
        gsap.set(targets, { clearProps: 'color,borderColor' });
        trigger.classList.toggle('is-saint-filled', filled);
        if (this.reduce) return;

        targets.forEach((element, index) => {
          const style = getComputedStyle(element);
          gsap.fromTo(element, from[index], {
            color: style.color,
            borderColor: style.borderColor,
            duration: 0.28,
            delay: filled ? 0.05 : 0,
            ease: 'power2.out',
            // 'auto', not true: true would also kill other tweens on the element (an entrance, a lift).
            overwrite: 'auto',
            clearProps: 'color,borderColor',
          });
        });
      }

      draw(trigger) {
        if (this.reduce || !this.dataset.draw) return;
        const strokes = trigger.querySelectorAll(this.dataset.draw);
        // Let a draw that is already running finish instead of restarting it.
        if (!strokes.length || gsap.isTweening(strokes)) return;

        gsap.fromTo(
          strokes,
          {
            strokeDasharray: (index, stroke) => stroke.getTotalLength(),
            strokeDashoffset: (index, stroke) => stroke.getTotalLength(),
          },
          {
            strokeDashoffset: 0,
            duration: 0.28,
            ease: 'power2.inOut',
            stagger: 0.03,
            clearProps: 'strokeDasharray,strokeDashoffset',
          }
        );
      }
    }
  );
}
